import { useNavigate } from "react-router-dom";
import { Key, Sparkles, Clock, ShieldCheck, MapPin, TrendingUp, Handshake, BadgeCheck } from "lucide-react";
import Reveal, { usePageMeta } from "@/components/Reveal";

const VALUES = [
  { icon: BadgeCheck, title: "Exigence", desc: "Chaque séjour est préparé avec le soin d'une maison d'hôtes : propreté irréprochable, linge de qualité, accueil soigné." },
  { icon: Handshake, title: "Confiance", desc: "Une relation transparente avec les propriétaires : reporting clair, revenus optimisés, bien respecté." },
  { icon: TrendingUp, title: "Performance", desc: "Tarification dynamique et annonces optimisées pour maximiser le taux d'occupation et les revenus locatifs." },
];

const MISSIONS = [
  { icon: Key, title: "Gestion locative complète", desc: "Création et optimisation des annonces, gestion du calendrier, des réservations et de la communication avec les voyageurs." },
  { icon: Sparkles, title: "Ménage & blanchisserie", desc: "Nettoyage professionnel entre chaque séjour et fourniture de linge de maison haut de gamme." },
  { icon: Clock, title: "Accueil & assistance 24/7", desc: "Remise des clés personnalisée ou arrivée autonome sécurisée, assistance disponible à tout moment." },
  { icon: ShieldCheck, title: "Protection du patrimoine", desc: "États des lieux rigoureux et vérifications systématiques pour préserver votre bien sur la durée." },
];

export default function About() {
  usePageMeta("À propos", "Golden Aura Pro, conciergerie spécialisée en location courte durée à Oyonnax, Lyon et alentours. Gestion locative haut de gamme pour propriétaires exigeants.");
  const navigate = useNavigate();

  return (
    <div data-testid="about-page">
      <section className="max-w-5xl mx-auto px-6 pt-24 sm:pt-32 pb-20 text-center">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-6">À propos</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F6F1E6] leading-tight mb-8">
            Une conciergerie <span className="italic gold-text">à la hauteur de votre bien</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-base md:text-lg text-[#B9BFD0] leading-relaxed max-w-3xl mx-auto">
            Golden Aura Pro est une conciergerie spécialisée dans la location de courte durée. Nous accompagnons
            les propriétaires dans la gestion complète de leur bien et offrons aux voyageurs des séjours
            confortables, sereins et mémorables. Notre terrain d'action :{" "}
            <span className="text-[#E5C158]">Oyonnax, Lyon et leurs alentours</span>.
          </p>
        </Reveal>
      </section>

      <section className="border-y border-[#D4AF37]/10 bg-[#0C1322]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <Reveal className="text-center mb-14">
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Nos valeurs</p>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-[#F6F1E6]">Le luxe de la tranquillité</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-[#D4AF37]/15 bg-[#101A2E]/60 p-8 text-center" data-testid={`value-card-${i}`}>
                  <v.icon size={28} className="text-[#D4AF37] mx-auto mb-5" />
                  <h3 className="font-display text-xl text-[#F6F1E6] mb-3">{v.title}</h3>
                  <p className="text-sm text-[#9AA3B5] leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal className="mb-14">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Nos missions</p>
          <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-[#F6F1E6] max-w-2xl">
            Tout ce que votre location mérite, <span className="italic gold-text">sans que vous ayez à y penser.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {MISSIONS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-[#D4AF37]/15 bg-[#101A2E]/60 p-8 transition-all duration-500 hover:border-[#D4AF37]/50" data-testid={`mission-card-${i}`}>
                <m.icon size={26} className="text-[#D4AF37] mb-5" />
                <h3 className="font-display text-lg text-[#F6F1E6] mb-3">{m.title}</h3>
                <p className="text-sm text-[#9AA3B5] leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <div className="rounded-3xl gold-frame bg-[#101A2E] p-10 sm:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <p className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">
                <MapPin size={14} /> Zone d'intervention
              </p>
              <p className="font-display text-2xl text-[#F6F1E6]">Oyonnax · Lyon · et alentours</p>
              <p className="text-sm text-[#9AA3B5] mt-3 max-w-lg">
                Propriétaire d'un appartement ou d'une maison ? Demandez une étude gratuite et sans engagement de votre potentiel locatif.
              </p>
            </div>
            <button onClick={() => navigate("/contact")} data-testid="about-cta-btn" className="gold-button rounded-full px-9 py-4 text-xs font-medium tracking-[0.2em] uppercase shrink-0">
              Demander une étude
            </button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
